import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Sparkles } from 'lucide-react';
import { RARITY_UI } from '../../lib/rarity';
import api from '../../lib/api';

// Higher index = rarer, matches backend rarity_system.RARITY_TIERS
const RARITY_RANK = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4, mythic: 5 };

export function cardImageUrl(card) {
  if (!card) return null;
  const raw = card.image_url || card.image_path || card.picture;
  if (raw) {
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/api/files/')) {
      return raw;
    }
    return `/api/files/${raw}`;
  }
  return card.art_url || null;
}

export default function ARCardPicker({ open, onClose, onPick, selectedId }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.get('/cards', { params: { limit: 200 } })
      .then(({ data }) => {
        if (cancelled) return;
        // Sort: rarity desc, then most recent
        const sorted = [...data].sort((a, b) => {
          const ra = RARITY_RANK[a.rarity] ?? -1;
          const rb = RARITY_RANK[b.rarity] ?? -1;
          if (rb !== ra) return rb - ra;
          return String(b.created_at || '').localeCompare(String(a.created_at || ''));
        });
        setCards(sorted.slice(0, 20));
      })
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return cards;
    return cards.filter(c =>
      (c.species_name || '').toLowerCase().includes(s) ||
      (c.common_name || '').toLowerCase().includes(s) ||
      (c.rarity || '').toLowerCase().includes(s),
    );
  }, [cards, q]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="ar-card-picker-backdrop"
          />
          <motion.div
            className="fixed inset-x-0 bottom-0 z-[101] rounded-t-3xl bg-neutral-950/95 border-t border-white/10 pb-[env(safe-area-inset-bottom)]"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            data-testid="ar-card-picker"
          >
            <div className="pt-2 flex justify-center"><div className="w-10 h-1 rounded-full bg-white/20" /></div>
            <div className="px-5 pt-3 pb-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-accent">Field-Team</p>
                <h2 className="font-serif text-xl leading-tight">Pick your Top Card</h2>
              </div>
              <button
                onClick={onClose}
                data-testid="ar-picker-close"
                className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition"
                aria-label="Close picker"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 pb-3">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder="Search species or rarity"
                  data-testid="ar-picker-search"
                  className="w-full pl-8 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-accent/60"
                />
              </div>
            </div>

            <div className="px-4 pb-6 max-h-[62vh] overflow-y-auto">
              {loading ? (
                <div className="py-10 flex items-center justify-center text-xs text-muted-foreground">Loading top cards…</div>
              ) : filtered.length === 0 ? (
                <div className="py-10 flex flex-col items-center justify-center gap-2 text-center">
                  <Sparkles className="w-6 h-6 text-accent" />
                  <p className="text-sm">No cards yet.</p>
                  <p className="text-xs text-muted-foreground max-w-[220px]">Discover wildlife first — your captures unlock field-team mode.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2.5" data-testid="ar-picker-grid">
                  {filtered.map((c, i) => {
                    const ui = RARITY_UI[c.rarity] || RARITY_UI.common;
                    const img = cardImageUrl(c);
                    const isSelected = selectedId === c.card_id;
                    return (
                      <motion.button
                        key={c.card_id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.02 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => onPick(c)}
                        data-testid={`ar-picker-card-${i}`}
                        className={`relative aspect-[3/4] rounded-xl overflow-hidden ${ui.borderClass}`}
                        style={{ ...ui.borderStyle, outline: isSelected ? '2px solid rgba(233,196,106,0.9)' : 'none' }}
                      >
                        {img ? (
                          <img src={img} alt={c.species_name || 'card'} className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                          <div className="absolute inset-0 bg-neutral-800" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                        <div className="absolute inset-x-1.5 bottom-1.5">
                          <p className="text-[10px] uppercase tracking-widest" style={{ color: ui.color }}>{ui.label}</p>
                          <p className="text-[11px] font-serif leading-tight line-clamp-2 text-white">{c.species_name || 'Wildlife'}</p>
                        </div>
                        {i === 0 && (
                          <span className="absolute top-1.5 left-1.5 text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-accent text-black font-mono font-bold">Top</span>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
